import { useState, type ReactNode } from 'react'
import cx from 'classnames'
import { isSupported, copy } from 'clipbrd'
import { Spinner } from '@/components/Spinner'
import { RawButton } from '@/components/Button'
import styles from './Section.module.css'

export interface SectionProps {
  title: string
  bordered?: boolean
  children: ReactNode
  isLoading?: boolean
  actions?: ReactNode
}

export function Section({
  title,
  children,
  bordered = false,
  isLoading,
  actions,
}: SectionProps) {
  return (
    <div>
      <div className={cx(bordered && styles.bordered)}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>{title}</div>
          {actions}
        </div>

        {isLoading ? (
          <div className={styles.loading}>
            <Spinner size={24} />
          </div>
        ) : (
          <div>{children}</div>
        )}
      </div>
    </div>
  )
}

export interface SectionItemProps {
  label: string
  value?: string
  maskedValue?: string
  isSensitive?: boolean
  copyable?: boolean
  showHideLabel?: string
}

export function SectionItem({
  label,
  value,
  maskedValue,
  isSensitive,
  copyable = true,
  showHideLabel = 'number',
}: SectionItemProps) {
  const hasMaskedValue = maskedValue !== undefined
  const [isVisible, setIsVisible] = useState(false)

  const toggleVisible = () => setIsVisible((prev) => !prev)
  const copyValue = () => {
    if (isSupported()) {
      copy(value ?? '')
    }
  }

  return (
    <div className={styles.contentItem}>
      <div className={styles.label}>{label}</div>
      <div className={styles.value}>
        <div
          className={styles.masked}
          {...((isSensitive || hasMaskedValue) && { 'data-private': 'true' })}
        >
          {hasMaskedValue && !isVisible ? maskedValue : value}
        </div>
        <div>
          {hasMaskedValue && (
            <RawButton className={styles.show} onPress={toggleVisible}>
              {isVisible ? `Hide ${showHideLabel}` : `Show ${showHideLabel}`}
            </RawButton>
          )}
          {hasMaskedValue && <span className={styles.divider}>|</span>}
          {copyable && (
            <RawButton className={styles.copy} onPress={copyValue}>
              Copy
            </RawButton>
          )}
        </div>
      </div>
    </div>
  )
}
